<template>
  <div>
    <div class="order-confirmation-form">
      <div class="d-flex order-confirmation-form__wrap">
        <div class=" order-confirmation-form__control col-1-3">
          <div class="label">{{ $t("page.order.confirmation.address.name1") }}</div>
          <v-input
            id="address-name1-input"
            v-model="addressForm.name1"
            :placeholder="$t('common.typehere')"
            :has-error="hasFieldError('name1')"
            @blur="$v.addressForm.name1.$touch"
          />
          <div v-if="hasFieldError('name1')" class="error-label">{{ $t("common.required") }}</div>
        </div>
        <div class="order-confirmation-form__control col-1-3">
          <div class="label">{{ $t("page.order.confirmation.address.name2") }}</div>
          <v-input id="address-name2-input" v-model="addressForm.name2" :placeholder="$t('common.typehere')" />
        </div>
        <div class="order-confirmation-form__control col-1-3">
          <div class="label">{{ $t("page.order.confirmation.address.namesuffix") }}</div>
          <v-input id="address-name-suffix-input" v-model="addressForm.nameSuffix" :placeholder="$t('common.typehere')" />
        </div>
      </div>
      <div class="d-flex order-confirmation-form__wrap">
        <div class="order-confirmation-form__control">
          <div class="label">{{ $t("page.order.confirmation.address.streethousenumber") }}</div>
          <v-input
            id="address-house-number-input"
            v-model="addressForm.streetHouseNumber"
            :placeholder="$t('common.typehere')"
            :has-error="hasFieldError('streetHouseNumber')"
            @blur="$v.addressForm.streetHouseNumber.$touch"
          />
          <div v-if="hasFieldError('streetHouseNumber')" class="error-label">
            {{ $t("common.required") }}
          </div>
        </div>
      </div>
      <div class="d-flex order-confirmation-form__wrap">
        <div class=" order-confirmation-form__control col-1-3">
          <div class="label">PLZ</div>
          <v-input
            id="address-zip-code-input"
            v-model="addressForm.zipCode"
            :placeholder="$t('common.typehere')"
            :has-error="hasFieldError('zipCode')"
            @blur="$v.addressForm.zipCode.$touch"
          />
          <div v-if="hasFieldError('zipCode')" class="error-label">
            {{ $t("common.required") }}
          </div>
        </div>
        <div class="order-confirmation-form__control col-2-3">
          <div class="label">{{ $t("page.order.confirmation.address.city") }}</div>
          <v-input
            id="address-city-input"
            v-model="addressForm.city"
            :placeholder="$t('common.typehere')"
            :has-error="hasFieldError('city')"
            @blur="$v.addressForm.city.$touch"
          />
          <div v-if="hasFieldError('city')" class="error-label">
            {{ $t("common.required") }}
          </div>
        </div>
      </div>
    </div>
    <div class="mt-50 text-right order-confirmation-popup__footer">
      <v-button
        v-if="address"
        class="mr-10 order-confirmation-form__delete button--white-border"
        :loading="deleteInProgress"
        @click="deleteOrder"
      >
        <img src="~@/assets/icons/icon-remove-blue.svg" class="icon-left" />
        {{ $t("common.delete") }}
      </v-button>

      <v-button v-else class="mr-10 order-confirmation-form__back" @click="$emit('cancel')" white>
        <img src="~@/assets/icons/arrow-right-blue.svg" class="icon-left" />
        {{ $t("common.back") }}
      </v-button>
      <v-button
        @click="saveAddress"
        :loading="loading"
        :disabled="$v.addressForm.$invalid || deleteInProgress"
        class="order-confirmation-form__continue"
        primary
      >
        {{ $t("common.continue") }}
        <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.28859 9.88047L5.16859 6.00047L1.28859 2.12047C0.898594 1.73047 0.898594 1.10047 1.28859 0.710469C1.67859 0.320469 2.30859 0.320469 2.69859 0.710469L7.28859 5.30047C7.67859 5.69047 7.67859 6.32047 7.28859 6.71047L2.69859 11.3005C2.30859 11.6905 1.67859 11.6905 1.28859 11.3005C0.908594 10.9105 0.898594 10.2705 1.28859 9.88047Z"
            fill="#FDFDFD"
          />
        </svg>
      </v-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { VInput, VButton } from "shared";
import { required } from "vuelidate/lib/validators";
import * as AddressesDAO from "../../dao/addresses-dao";

export default {
  name: "VOrderConfirmationForm",
  components: {
    VInput,
    VButton
  },
  props: {
    address: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      deleteInProgress: false,
      addressForm: {
        name1: "",
        name2: "",
        nameSuffix: "",
        streetHouseNumber: "",
        zipCode: "",
        city: ""
      }
    };
  },

  computed: {
    ...mapGetters("order", ["basicData"])
  },

  methods: {
    hasFieldError(field) {
      return (
        this.$v.addressForm[field].$dirty && this.$v.addressForm[field].$invalid && !this.$v.addressForm[field].required
      );
    },
    async deleteOrder() {
      this.deleteInProgress = true;
      await AddressesDAO.deleteAddress(this.address.id);
      this.deleteInProgress = false;
      this.$emit("deleted", this.address.id);
    },
    async saveAddress() {
      this.$v.addressForm.$touch();
      if (!this.$v.addressForm.$invalid) {
        this.loading = true;

        const addressForm = { ...this.addressForm, country: this.basicData.deliveryCountry };
        if (this.address) {
          await AddressesDAO.updateAddress(addressForm);
          this.loading = false;
          this.$emit("updated", addressForm);
        } else {
          const { id } = await AddressesDAO.createAddress(addressForm);
          this.loading = false;
          this.$emit("created", id);
        }
      }
    }
  },

  created() {
    if (this.address) {
      this.addressForm = Object.assign(this.address);
    }
  },

  validations: {
    addressForm: {
      name1: { required },
      streetHouseNumber: { required },
      zipCode: { required },
      city: { required }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";
.order-confirmation-form {
  &__wrap {
    margin: 0 0 22px 0;
    justify-content: space-between;
  }
  .col-1-3 {
    width: 32%;
  }
  .col-2-3 {
    width: 66%;
  }
  &__delete {
    padding: 8px 15px 9px 35px;
    position: relative;
    top: 5px;

    img {
      height: 20px;
    }
  }
  &__back {
    padding-left: 35px;
    padding-right: 15px;
    img {
      width: 10px;
      height: 10px;
      transform: rotate(180deg);
      margin-top: -1px !important;
    }
  }
  &__control {
    width: 100%;
    position: relative;
    .error-label {
      position: absolute;
      color: #ff0000;
      font-size: 12px;
      padding: 2px 0 0 10px;
    }
    .label {
      font-size: 13px;
      line-height: 14px;
      color: $color-medium-grey;
      display: inline-block;
      margin: 0 0 7px 0;
      position: relative;
      .tooltip {
        left: 100%;
        bottom: 100%;
      }
      .icon-info {
        width: 15px;
        margin: 0 0 -2px 0;
      }
      &:hover {
        .tooltip {
          opacity: 1;
          visibility: visible;
        }
      }
    }
    &::v-deep .input {
      height: 42px;
      width: 100%;
      background: #fdfdfd;
      border-radius: 3px;
    }
    span {
      width: 100%;
      display: block;
    }
  }
}
.order-confirmation-popup {
  &__footer {
    position: absolute;
    left: 0;
    right: 64px;
    bottom: 64px;
  }
}
.tooltip {
  position: absolute;
  left: 0;
  bottom: 100%;
  background: #fff url("~@/assets/icons/icon-info.svg") 16px 18px;
  background-repeat: no-repeat;
  box-shadow: 0px 2px 4px rgba(153, 153, 153, 0.4);
  border-radius: 6px 6px 0px 6px;
  width: 355px;
  padding: 18px 10px 15px 57px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
}
</style>
